import React, { useMemo, useState } from 'react';
import { Modal, View, KeyboardAvoidingView, Platform, Dimensions } from 'react-native';
import { planFeatures, type Profile } from '@ventureroom/core';
import { useApp, useData, useMe, useTheme } from './provider';
import { Avatar, Button, Card, Column, Copy, Empty, Heading, Metric, Page, Row } from './components';
import { Conversation } from './connections';

const WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Everything a subscription unlocks lives here. Each section only renders when the plan
 * includes it, so a lower tier never sees (or reaches) a higher tier's tools.
 */
export function PlanToolsScreen() {
  const me = useMe();
  const app = useApp();
  const f = planFeatures(me);
  const any = f.analyticsBasic || f.manageTeam || f.websiteStats || f.teamDigest || f.boardroom;
  if (!any)
    return (
      <Page title="Plan tools" back="dashboard">
        <Empty
          title="No plan tools yet"
          detail="Tools you unlock with a subscription will appear here."
          action={<Button onPress={() => app.navigate('settings')}>View plans</Button>}
        />
      </Page>
    );
  return (
    <Page 
      title="Plan tools" 
      back="dashboard"
      action={f.manageTeam ? <Button compact onPress={() => app.navigate(`meeting/${me.id}`)}>Meeting room</Button> : undefined}
    >
      <Column gap={32}>
        {f.manageTeam && <TeamSection me={me} />}
        {f.analyticsBasic && <AnalyticsSection me={me} advanced={f.analyticsAdvanced} />}
        {(f.websiteStats || f.boardroom) && <VentureToolsShortcut f={f} />}
        {f.teamDigest && <DigestSection me={me} />}
      </Column>
    </Page>
  );
}

function VentureToolsShortcut({ f }: { f: ReturnType<typeof planFeatures> }) {
  const data = useData();
  const me = useMe();
  const t = useTheme();
  const app = useApp();
  const [boardroomVenture, setBoardroomVenture] = useState<string | null>(null);
  const mine = data.ventures.filter((v) => v.members.includes(me.id) && v.stage !== 'Closed');

  return (
    <>
      <Column gap={12}>
        <Heading size={22}>Venture Tools</Heading>
        <Copy muted size={13}>
          Access premium tools for your active ventures.
        </Copy>
        {mine.length === 0 ? (
          <Copy muted>Start a venture to use this.</Copy>
        ) : (
          mine.map((v) => (
            <Card key={v.id}>
              <Row between>
                <Heading size={18}>{v.name}</Heading>
                <Row style={{ gap: 8 }}>
                  {f.websiteStats && (
                    <Button compact onPress={() => app.navigate(`venture/${v.id}`)}>
                      Access
                    </Button>
                  )}
                  {f.boardroom && (
                    <Button compact onPress={() => setBoardroomVenture(v.id)}>
                      Open Boardroom
                    </Button>
                  )}
                </Row>
              </Row>
            </Card>
          ))
        )}
      </Column>
      {boardroomVenture && (
        <Modal transparent animationType="slide" onRequestClose={() => setBoardroomVenture(null)}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: Dimensions.get('window').width > 650 ? 210 : 0, backgroundColor: 'rgba(0,0,0,0.5)', padding: 20, justifyContent: 'center' }}
          >
            <View style={{ flex: 1, height: '100%', backgroundColor: t.background, borderRadius: 24, overflow: 'hidden', alignSelf: 'center', width: '100%', maxWidth: 1000, maxHeight: '94%' }}>
              <Row between style={{ padding: 20, borderBottomWidth: 1, borderColor: t.border }}>
                 <Heading size={22}>AI Boardroom</Heading>
                 <Button kind="primary" compact onPress={() => setBoardroomVenture(null)}>Close</Button>
              </Row>
              <View style={{ flex: 1, paddingHorizontal: 20, paddingBottom: 20 }}>
                <Conversation channel={`boardroom-${boardroomVenture}`} boardroom />
              </View>
            </View>
          </KeyboardAvoidingView>
        </Modal>
      )}
    </>
  );
}

function AnalyticsSection({ me, advanced }: { me: Profile; advanced: boolean }) {
  const data = useData();
  const stats = me.stats ?? { profileViews: 0, searchAppearances: 0 };
  const requests = data.connections.filter((c) => c.to === me.id).length;
  const connected = data.connections.filter(
    (c) => c.status === 'accepted' && [c.from, c.to].includes(me.id),
  ).length;
  const endorsements = data.endorsements.filter((e) => e.to === me.id).length;
  const conversion =
    stats.profileViews > 0 ? `${Math.min(100, Math.round((requests / stats.profileViews) * 100))}%` : '-';
  const demand = useMemo(() => {
    const counts = new Map<string, number>();
    const add = (skill: string) => counts.set(skill, (counts.get(skill) ?? 0) + 1);
    for (const v of data.ventures) if (v.stage !== 'Closed') v.seeking.forEach(add);
    for (const p of data.profiles)
      if (p.id !== me.id && p.role !== 'Skill Provider') p.seeking.forEach(add);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [data.ventures, data.profiles, me.id]);
  const mine = new Set(me.skills.map((s) => s.toLowerCase()));
  return (
    <Column gap={12}>
      <Heading size={22}>{advanced ? 'Advanced analytics' : 'Analytics'}</Heading>
      <Row wrap>
        <Card style={{ flex: 1, minWidth: 150 }}>
          <Metric value={stats.profileViews} label="profile views" />
        </Card>
        <Card style={{ flex: 1, minWidth: 150 }}>
          <Metric value={stats.searchAppearances} label="search appearances" />
        </Card>
        {advanced && (
          <>
            <Card style={{ flex: 1, minWidth: 150 }}>
              <Metric value={conversion} label="conversion rate" />
            </Card>
            <Card style={{ flex: 1, minWidth: 150 }}>
              <Metric value={requests} label="requests received" />
            </Card>
            <Card style={{ flex: 1, minWidth: 150 }}>
              <Metric value={connected} label="connections" />
            </Card>
            <Card style={{ flex: 1, minWidth: 150 }}>
              <Metric value={endorsements} label="endorsements" />
            </Card>
          </>
        )}
      </Row>
      {advanced && (
        <Card>
          <Column gap={8}>
            <Heading size={18}>Market demand</Heading>
            <Copy muted size={13}>
              Skills founders are asking for right now. Conversion rate is requests received divided by
              profile views.
            </Copy>
            {demand.length === 0 ? (
              <Copy muted>No demand data yet.</Copy>
            ) : (
              demand.map(([skill, count]) => (
                <Row key={skill} between>
                  <Copy bold={mine.has(skill.toLowerCase())}>
                    {skill}
                    {mine.has(skill.toLowerCase()) ? ' (you offer this)' : ''}
                  </Copy>
                  <Copy muted>
                    {count} {count === 1 ? 'request' : 'requests'}
                  </Copy>
                </Row>
              ))
            )}
          </Column>
        </Card>
      )}
    </Column>
  );
}

function TeamSection({ me }: { me: Profile }) {
  const data = useData();
  const app = useApp();
  const teamIds = me.team ?? [];
  const team = teamIds
    .map((id) => data.profiles.find((p) => p.id === id))
    .filter((p): p is Profile => !!p);
  const candidates = new Map<string, Profile>();
  for (const c of data.connections) {
    if (c.status !== 'accepted' || ![c.from, c.to].includes(me.id)) continue;
    const other = data.profiles.find((p) => p.id === (c.from === me.id ? c.to : c.from));
    if (other && other.role !== 'Business Owner' && !teamIds.includes(other.id))
      candidates.set(other.id, other);
  }
  const person = (p: Profile, action: 'add' | 'remove') => (
    <Card key={p.id}>
      <Row between>
        <Row style={{ flex: 1, minWidth: 0 }}>
          <Avatar person={p} size={44} />
          <Column gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Heading size={18}>{p.name}</Heading>
            <Copy muted size={13} lines={1}>
              {p.skills.join(', ')}
            </Copy>
          </Column>
        </Row>
        <Button
          compact
          kind={action === 'add' ? 'primary' : 'danger'}
          onPress={() =>
            void app.act(
              { type: 'teamMember', action, personId: p.id },
              action === 'add' ? `${p.name} joined your team` : `${p.name} removed from your team`,
            )
          }
        >
          {action === 'add' ? 'Add to team' : 'Remove'}
        </Button>
      </Row>
    </Card>
  );
  return (
    <Column gap={12}>
      <Heading size={22}>Manage team</Heading>
      <Copy muted size={13}>
        Build your own agency from people you are connected with. Your team is shown on your profile.
      </Copy>
      {team.length === 0 ? (
        <Copy muted>Your team is empty. Add a connection below to get started.</Copy>
      ) : (
        <Column gap={10}>{team.map((p) => person(p, 'remove'))}</Column>
      )}
      <Heading size={18}>Add from your connections</Heading>
      {candidates.size === 0 ? (
        <Empty
          title="No one to add yet"
          detail="Connect with other skill providers in Discover and they will appear here."
          action={<Button onPress={() => app.navigate('discover')}>Go to Discover</Button>}
        />
      ) : (
        <Column gap={10}>{[...candidates.values()].map((p) => person(p, 'add'))}</Column>
      )}
    </Column>
  );
}


function DigestSection({ me }: { me: Profile }) {
  const data = useData();
  const since = Date.now() - WEEK;
  const recent = (iso?: string) => !!iso && Date.parse(iso) >= since;
  const mine = data.ventures.filter((v) => v.members.includes(me.id) && v.stage !== 'Closed');
  return (
    <Column gap={12}>
      <Heading size={22}>Team digest</Heading>
      <Copy muted size={13}>
        What each of your teams got done over the last 7 days.
      </Copy>
      {mine.length === 0 ? (
        <Copy muted>Start a venture to see its weekly digest.</Copy>
      ) : (
        mine.map((v) => {
          const tasks = data.commitments.filter((c) => c.ventureId === v.id);
          const open = tasks.filter((c) => c.status !== 'Completed' && c.status !== 'Cancelled');
          return (
            <Card key={v.id}>
              <Column gap={10}>
                <Heading size={18}>{v.name}</Heading>
                <Row wrap>
                  <Metric value={tasks.filter((c) => recent(c.completedAt)).length} label="tasks completed" />
                  <Metric value={tasks.filter((c) => recent(c.createdAt)).length} label="tasks added" />
                  <Metric
                    value={data.contributions.filter((c) => c.ventureId === v.id && recent(c.createdAt)).length}
                    label="contributions"
                  />
                  <Metric
                    value={data.decisions.filter((d) => d.ventureId === v.id && recent(d.createdAt)).length}
                    label="decisions opened"
                  />
                </Row>
                <Copy size={14}>Next milestone: {v.milestone}</Copy>
                {open.slice(0, 3).map((c) => (
                  <Copy key={c.id} muted size={13}>
                    Open: {c.title} ({data.profiles.find((p) => p.id === c.owner)?.name ?? 'Unassigned'})
                  </Copy>
                ))}
              </Column>
            </Card>
          );
        })
      )}
    </Column>
  );
}
